import { has, isEmpty, get } from 'lodash';
import { format } from 'date-fns';

import { getModeOfSale, getLoginModeOfSale, getCartCount } from '../utils/cookies';
import zonedDate from '../utils/zoned-date';

const defaultPerformanceModeOfSale = 4;

const defaultPackageModesOfSale = {
    series: 26,
    createYourOwn: 5,
}

const specialPackageModesOfSale = {
    47: {
        series: 47,
        createYourOwn: 5,
    },
    48: {
        series: 47,
        createYourOwn: 5,
    },
}

const availabilityOverrideWebContentId = 24;

export const bookingStatuses = {
    feedError: 'feedError',
    notApplicable: 'notApplicable',
    notOnSale: 'notOnSale',
    onSale: 'onSale',
    soldOut: 'soldOut',
    offSale: 'offSale',
}

export const buttonStates = {
    singleSubsCyoOnSale: 'singleSubsCyoOnSale',
    singleSubsOnSale: 'singleSubsOnSale',
    singleCyoOnSale: 'singleCyoOnSale',
    singleOnSale: 'singleOnSale',
    subsCyoOnSale: 'subsCyoOnSale',
    subsOnSale: 'subsOnSale',
    cyoOnSale: 'cyoOnSale',
    notOnSale: 'notOnSale',
    soldOut: 'soldOut',
    noButtons: 'noButtons',
    noLongerAvailable: 'noLongerAvailable',
    custom: 'custom',
}

const now = zonedDate();

let _performanceCache = null;
let _packageCache = null;
let _createYourOwnCache = null;
let _progressiveCreateYourOwnCache = null;

const feedErrorRetryDelay = 30 * 1000;

// fetchData resolves to feedError rather than rejecting. Keep that result long
// enough for the current render to reuse it, then let the next call try again
function retryAfterFeedError(promise, forget) {
    promise.then(response => {
        if (response === bookingStatuses.feedError) {
            setTimeout(forget, feedErrorRetryDelay);
        }
    });

    return promise;
}

function determinePerformanceModeOfSale() {
    const loginModeOfSale = getLoginModeOfSale();
    const currentModeOfSale = getModeOfSale();
    const cartCount = getCartCount();

    if (loginModeOfSale && loginModeOfSale === currentModeOfSale) {
        return currentModeOfSale;
    }

    if (currentModeOfSale && cartCount) {
        return currentModeOfSale;
    }

    return defaultPerformanceModeOfSale;
}

function determinePackageModesOfSale() {
    const performanceModeOfSale = determinePerformanceModeOfSale();

    if (has(specialPackageModesOfSale, performanceModeOfSale)) {
        return specialPackageModesOfSale[performanceModeOfSale];
    }

    return defaultPackageModesOfSale;
}

async function fetchData(resource, propertyKey, idKey) {
    const url = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/en${resource}&origin=${window.location.host}`;

    return fetch(url, { mode: 'cors' }).then(response => { // eslint-disable-line
        if (!response.ok) {
            throw new Error(bookingStatuses.feedError);
        }

        return response.json();
    }).then(response => {
        if (isEmpty(response)) {
            return {}
        }

        const inventory = get(response, propertyKey, []);
        const webContents = get(response, 'WebContent', []);

        const state = {}

        for (const item of inventory) {
            const inventoryId = parseInt(get(item, idKey), 10);

            const availabilityOverride = webContents.find(webContent => {
                const forItem = (
                    parseInt(webContent.orig_inv_no, 10) === inventoryId ||
                    parseInt(webContent.inv_no, 10) === inventoryId
                );

                return forItem && parseInt(webContent.content_type, 10) === availabilityOverrideWebContentId;
            });

            const isAvailable = (item.pricing || []).some(price => {
                return get(price, 'tickets_available') === true;
            });

            let isAvailableOverride = null;

            if (availabilityOverride) {
                const value = availabilityOverride.content_value?.toLowerCase();

                if (value === 'available') {
                    isAvailableOverride = true;
                } else if (value === 'unavailable') {
                    isAvailableOverride = false;
                }
            }

            const onSaleDate = zonedDate(item.start_dt);
            const offSaleDate = zonedDate(item.end_dt);

            state[inventoryId] = {
                onSaleDate,
                onSaleDateFormatted: format(onSaleDate, 'MMM d, yyyy'),
                offSaleDate,
                isAvailable,
                isAvailableOverride,
            }
        }

        return state;
    }).catch(() => {
        return bookingStatuses.feedError;
    });
}

async function loadPerformanceData() {
    if (_performanceCache) {
        return _performanceCache;
    }

    _performanceCache = retryAfterFeedError(fetchData(`/api/productions?mos=${determinePerformanceModeOfSale()}`, 'Performance', 'perf_no'), () => {
        _performanceCache = null;
    });

    return _performanceCache;
}

async function loadPackageData() {
    if (_packageCache) {
        return _packageCache;
    }

    _packageCache = retryAfterFeedError(fetchData(`/api/packages?mos=${determinePackageModesOfSale().series}`, 'Package', 'pkg_no'), () => {
        _packageCache = null;
    });

    return _packageCache;
}

async function loadCreateYourOwnData() {
    if (_createYourOwnCache) {
        return _createYourOwnCache;
    }

    _createYourOwnCache = retryAfterFeedError(fetchData(`/api/packages?mos=${determinePackageModesOfSale().createYourOwn}`, 'Package', 'pkg_no'), () => {
        _createYourOwnCache = null;
    });

    return _createYourOwnCache;
}

async function loadProgressiveCreateYourOwnData() {
    if (_progressiveCreateYourOwnCache) {
        return _progressiveCreateYourOwnCache;
    }

    _progressiveCreateYourOwnCache = retryAfterFeedError(fetchData(`/api/productions?mos=${determinePackageModesOfSale().createYourOwn}`, 'Performance', 'perf_no'), () => {
        _progressiveCreateYourOwnCache = null;
    });

    return _progressiveCreateYourOwnCache;
}

async function getPerformance(performanceId) {
    if (!performanceId) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    const performanceData = await loadPerformanceData();

    if (performanceData === bookingStatuses.feedError) {
        return {
            status: bookingStatuses.feedError,
        }
    }

    const performance = get(performanceData, performanceId);

    if (!performance) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    if (!performance.onSaleDate || !performance.offSaleDate) {
        // No start or end date
        performance.status = bookingStatuses.notApplicable;
    } else if (+performance.onSaleDate > +now) {
        // Not yet on sale
        performance.status = bookingStatuses.notOnSale;
    } else if (+performance.offSaleDate < +now) {
        // Off sale
        performance.status = bookingStatuses.offSale;
    } else if (performance.isAvailable === false) {
        // Sold out
        performance.status = bookingStatuses.soldOut;
    } else {
        // On sale
        performance.status = bookingStatuses.onSale;
    }

    // Availability override
    if (performance.isAvailableOverride === true) {
        performance.status = bookingStatuses.onSale;
    } else if (performance.isAvailableOverride === false) {
        performance.status = bookingStatuses.notOnSale;
    }

    // performance.bookingLabel = 'Buy Tickets';
    performance.bookingUrl = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/syos2/performance/${performanceId}`;

    return performance;
}

async function getSeries(seriesId, useBestAvailable = false) {
    if (!seriesId) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    const seriesData = await loadPackageData();

    if (seriesData === bookingStatuses.feedError) {
        return {
            status: bookingStatuses.feedError,
        }
    }

    const series =  seriesData[seriesId];

    if (!series) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    if (!series.onSaleDate || !series.offSaleDate) {
        // No start or end date
        series.status = bookingStatuses.notApplicable;
    } else if (+series.onSaleDate > +now) {
        // Not yet on sale
        series.status = bookingStatuses.notOnSale;
    } else if (+series.offSaleDate < +now) {
        // Off sale
        series.status = bookingStatuses.offSale;
    } else if (series.isAvailable === false) {
        // Sold out
        series.status = bookingStatuses.soldOut;
    } else {
        // On sale
        series.status = bookingStatuses.onSale;
    }

    if (useBestAvailable) {
        series.bookingUrl = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/booking/package/bestavailable/${seriesId}`;
    } else {
        series.bookingUrl = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/syos2/package/${seriesId}`;
    }

    return series;
}

async function getCreateYourOwn(createYourOwnId, performanceId) {
    if (!createYourOwnId || !performanceId) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    const [
        createYourOwnData,
        createYourOwnPerformanceData,
    ] = await Promise.all([
        loadCreateYourOwnData(),
        loadProgressiveCreateYourOwnData(),
    ]);

    if (createYourOwnData === bookingStatuses.feedError) {
        return {
            status: bookingStatuses.feedError,
        }
    }

    const createYourOwn = createYourOwnData[createYourOwnId];

    if (!createYourOwn) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    const createYourOwnPerformance = createYourOwnPerformanceData[performanceId];

    if (!createYourOwn.onSaleDate || !createYourOwn.offSaleDate) {
        // No start or end date
        createYourOwn.status = bookingStatuses.notApplicable;
    } else if (+createYourOwn.onSaleDate > +now) {
        // Not yet on sale
        createYourOwn.status = bookingStatuses.notOnSale;
    } else if (+createYourOwn.offSaleDate < +now) {
        // Off sale
        createYourOwn.status = bookingStatuses.offSale;
    } else if (createYourOwnPerformance?.isAvailable === false) {
        // Sold out
        createYourOwn.status = bookingStatuses.soldOut;
    } else {
        // On sale
        createYourOwn.status = bookingStatuses.onSale;
    }

    createYourOwn.bookingUrl = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/booking/cyo/package/${createYourOwnId}`;

    return createYourOwn;
}

async function getProgressiveCreateYourOwn(performanceId) {
    if (!performanceId) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    const [
        createYourOwnPerformanceData,
        performance,
    ] = await Promise.all([
        loadProgressiveCreateYourOwnData(),
        getPerformance(performanceId),
    ]);

    if (createYourOwnPerformanceData === bookingStatuses.feedError) {
        return {
            status: bookingStatuses.feedError,
        }
    }

    const createYourOwn = get(createYourOwnPerformanceData, performanceId);

    if (!createYourOwn) {
        return {
            status: bookingStatuses.notApplicable,
        }
    }

    if (!createYourOwn.onSaleDate || !createYourOwn.offSaleDate) {
        // No start or end date
        createYourOwn.status = bookingStatuses.notApplicable;
    } else if (+createYourOwn.onSaleDate > +now) {
        // Not yet on sale
        createYourOwn.status = bookingStatuses.notOnSale;
    } else if (+createYourOwn.offSaleDate < +now) {
        // Off sale
        createYourOwn.status = bookingStatuses.offSale;
    } else if (createYourOwn.isAvailable === false) {
        // Sold out
        createYourOwn.status = bookingStatuses.soldOut;
    } else {
        // On sale
        createYourOwn.status = bookingStatuses.onSale;
    }

    // Availability override
    if (createYourOwn.isAvailableOverride === true) {
        createYourOwn.status = bookingStatuses.onSale;
    } else if (createYourOwn.isAvailableOverride === false) {
        createYourOwn.status = bookingStatuses.notOnSale;
    }

    if (performance?.status === bookingStatuses.soldOut || performance?.status === bookingStatuses.notOnSale) {
        createYourOwn.bookingUrl = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/syos2/cyo/performance/${performanceId}`;
    } else {
        createYourOwn.bookingUrl = `${window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '')}/syos2/performance/${performanceId}`;
    }

    return createYourOwn;
}

export async function getBookingState({
    performanceId,
    seriesId = null,
    createYourOwnId = null,
    useBestAvailableForSeries = false,
    useProgressiveCreateYourOwn = false,
} = {}) {
    const [ performance, series, createYourOwn ] = await Promise.all([
        getPerformance(performanceId),
        getSeries(seriesId, useBestAvailableForSeries),
        useProgressiveCreateYourOwn ? getProgressiveCreateYourOwn(performanceId) : getCreateYourOwn(createYourOwnId, performanceId),
    ]);

    const hasSingle = performance.status !== bookingStatuses.notApplicable;
    const hasSeries = series.status !== bookingStatuses.notApplicable;
    const hasCreateYourOwn = createYourOwn.status !== bookingStatuses.notApplicable;

    let state = buttonStates.noButtons;

    if (hasSingle && hasSeries && hasCreateYourOwn) {
        if (performance.status === bookingStatuses.soldOut && series.status === bookingStatuses.soldOut && createYourOwn.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (performance.status === bookingStatuses.offSale && series.status === bookingStatuses.offSale && createYourOwn.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (performance.status === bookingStatuses.notOnSale && series.status === bookingStatuses.notOnSale && createYourOwn.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (performance.status === bookingStatuses.onSale && series.status === bookingStatuses.onSale && createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.singleSubsCyoOnSale;
        } else

        if (performance.status === bookingStatuses.onSale && series.status === bookingStatuses.onSale) {
            state = buttonStates.singleSubsOnSale;
        } else

        if (performance.status === bookingStatuses.onSale && createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.singleCyoOnSale;
        } else

        if (series.status === bookingStatuses.onSale && createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.subsCyoOnSale;
        } else

        if (performance.status === bookingStatuses.onSale) {
            state = buttonStates.singleOnSale;
        } else

        if (series.status === bookingStatuses.onSale) {
            state = buttonStates.subsOnSale;
        } else

        if (createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.cyoOnSale;
        } else

        if (performance.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        }
    } else if (hasSingle && hasSeries) {
        if (performance.status === bookingStatuses.soldOut && series.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (performance.status === bookingStatuses.offSale && series.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (performance.status === bookingStatuses.notOnSale && series.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (performance.status === bookingStatuses.onSale && series.status === bookingStatuses.onSale) {
            state = buttonStates.singleSubsOnSale;
        } else

        if (performance.status === bookingStatuses.onSale) {
            state = buttonStates.singleOnSale;
        } else

        if (series.status === bookingStatuses.onSale) {
            state = buttonStates.subsOnSale;
        }

        if (performance.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        }
    } else if (hasSingle && hasCreateYourOwn) {
        if (performance.status === bookingStatuses.soldOut && createYourOwn.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (performance.status === bookingStatuses.offSale && createYourOwn.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (performance.status === bookingStatuses.notOnSale && createYourOwn.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (performance.status === bookingStatuses.onSale && createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.singleCyoOnSale;
        } else

        if (performance.status === bookingStatuses.onSale) {
            state = buttonStates.singleOnSale;
        } else

        if (createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.cyoOnSale;
        }

        if (performance.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        }
    } else if (hasSeries && hasCreateYourOwn) {
        if (series.status === bookingStatuses.soldOut && createYourOwn.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (series.status === bookingStatuses.offSale && createYourOwn.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (series.status === bookingStatuses.notOnSale && createYourOwn.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (series.status === bookingStatuses.onSale && createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.subsCyoOnSale;
        } else

        if (series.status === bookingStatuses.onSale) {
            state = buttonStates.subsOnSale;
        } else

        if (createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.cyoOnSale;
        }
    } else if (hasSingle) {
        if (performance.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (performance.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (performance.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (performance.status === bookingStatuses.onSale) {
            state = buttonStates.singleOnSale;
        }
    } else if (hasSeries) {
        if (series.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (series.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (series.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (series.status === bookingStatuses.onSale) {
            state = buttonStates.subsOnSale;
        }
    } else if (hasCreateYourOwn) {
        if (createYourOwn.status === bookingStatuses.soldOut) {
            state = buttonStates.soldOut;
        } else

        if (createYourOwn.status === bookingStatuses.offSale) {
            state = buttonStates.noLongerAvailable;
        } else

        if (createYourOwn.status === bookingStatuses.notOnSale) {
            state = buttonStates.notOnSale;
        } else

        if (createYourOwn.status === bookingStatuses.onSale) {
            state = buttonStates.cyoOnSale;
        }
    }

    return {
        state,
        performance,
        series,
        createYourOwn,
    }
}

export async function getPerformanceBookingState(performanceId) {
    return getPerformance(performanceId);
}

export async function getSeriesBookingState(seriesId, useBestAvailableForSeries = false) {
    return getSeries(seriesId, useBestAvailableForSeries);
}

export default {
    bookingStatuses,
    buttonStates,
    getBookingState,
    getPerformanceBookingState,
    getSeriesBookingState,
}
